<template>
	<el-container class="service-page">
		<resizable-aside :default-width="260" :min-width="200" :max-width="500" storage-key="service-aside-width">
			<el-scrollbar class="service-items">
				<div v-for="(services, i) in serviceValues" :key="i">
					<div class="letter">{{ serviceKeys[i] }}</div>
					<div v-for="service in services" :key="service.id">
						<service-item :service="service" :active="service.id === activeService.id"
							@select="onActiveItem"></service-item>
					</div>
					<div v-if="i < serviceValues.length - 1" class="divider"></div>
				</div>
				<no-data-tip v-if="serviceKeys.length == 0" :tip="$t('components.Service.noService')"></no-data-tip>
			</el-scrollbar>
		</resizable-aside>
		<el-container v-if="activeService.id" class="container">
			<div class="header" :class="configStore.electronMode ? 'header-menu-wrap' : ''">
				<div class="nick-name">{{ activeShowName }}</div>
				<span v-if="activeService.companyName" class="company-tag">@{{ activeService.companyName }}</span>
			</div>
			<div class="service-info">
				<div class="info-card">
					<div class="avatar-section">
						<head-image :size="120" :name="activeShowName" :url="activeService.headImage" radius="50%"
							:is-show-user-info="false"></head-image>
					</div>
					<div class="info-section">
						<div class="name-header">
							<h2 class="service-name">{{ activeShowName }}</h2>
							<div v-if="activeService.tag" class="service-tag">{{ activeService.tag }}</div>
						</div>
						<div class="info-item-list">
							<div v-if="activeService.companyName" class="info-item">
								<span class="info-label">{{ $t('components.Service.company') }}</span>
								<span class="info-value">{{ activeService.companyName }}</span>
							</div>
							<div v-if="activeService.detail" class="info-item">
								<span class="info-label">{{ $t('components.Service.detail') }}</span>
								<span class="info-value">{{ activeService.detail }}</span>
							</div>
						</div>
						<div class="btn-group">
							<el-button icon="el-icon-position" type="primary" @click="onSendMessage(activeService)">
								{{ $t('components.Service.sendMessage') }}
							</el-button>
						</div>
					</div>
				</div>
			</div>
		</el-container>
		<el-container v-else class="container empty-container">
			<no-data-tip :tip="$t('components.Service.selectTip')"></no-data-tip>
		</el-container>
	</el-container>
</template>

<script>
import { pinyin } from 'pinyin-pro';
import ServiceItem from "../components/service/ServiceItem.vue";
import HeadImage from "../components/common/HeadImage.vue";
import NoDataTip from "../components/common/NoDataTip.vue";
import ResizableAside from "../components/common/ResizableAside.vue";

export default {
	name: "service",
	components: {
		ServiceItem,
		HeadImage,
		NoDataTip,
		ResizableAside
	},
	data() {
		return {
			activeService: {}
		}
	},
	methods: {
		onActiveItem(service) {
			this.activeService = service;
		},
		onSendMessage(service) {
			let chat = {
				type: 'PRIVATE',
				targetId: service.id,
				showName: service.showNickName || service.name,
				headImage: service.headImage,
				companyName: service.companyName
			};
			this.chatStore.openChat(chat);
			this.chatStore.setActiveChat(chat);
			this.$router.push("/home/chat");
		},
		firstLetter(strText) {
			// 使用pinyin-pro库将中文转换为拼音
			let pinyinOptions = {
				toneType: 'none', // 无声调
				type: 'normal' // 普通拼音
			};
			let pyText = pinyin(strText, pinyinOptions);
			return pyText[0];
		},
		isEnglish(character) {
			return /^[A-Za-z]+$/.test(character);
		}
	},
	computed: {
		activeShowName() {
			return this.activeService.showNickName || this.activeService.name;
		},
		serviceMap() {
			// 按首字母分组
			let map = new Map();
			this.serviceStore.services.forEach((s) => {
				const name = s.showNickName || s.name || '';
				if (s.deleted) {
					return;
				}
				let letter = this.firstLetter(name).toUpperCase();
				// 非英文一律为#组
				if (!this.isEnglish(letter)) {
					letter = "#"
				}
				if (map.has(letter)) {
					map.get(letter).push(s);
				} else {
					map.set(letter, [s]);
				}
			})
			// 排序
			let arrayObj = Array.from(map);
			arrayObj.sort((a, b) => {
				// #组在最后面
				if (a[0] == '#' || b[0] == '#') {
					return b[0].localeCompare(a[0])
				}
				return a[0].localeCompare(b[0])
			})
			map = new Map(arrayObj.map(i => [i[0], i[1]]));
			return map;
		},
		serviceKeys() {
			return Array.from(this.serviceMap.keys());
		},
		serviceValues() {
			return Array.from(this.serviceMap.values());
		}
	}
}
</script>

<style lang="scss">
.service-page {

	.divider {
		border-bottom: 1px #ddd solid;
		margin: 10px;
	}

	.service-items {
		flex: 1;

		.no-data-tip {
			padding-top: 60px;
		}
	}

	.letter {
		text-align: left;
		font-size: var(--im-font-size-larger);
		padding: 5px 15px;
		color: var(--im-text-color-light);
	}

	.empty-container {
		align-items: center;
		justify-content: center;
	}

	.container {
		display: flex;
		flex-direction: column;
		background: #fcfdff;

		.header {
			height: 60px;
			line-height: 60px;
			display: flex;
			align-items: center;
			padding: 0 12px;
			box-sizing: border-box;

			.nick-name {
				font-size: var(--im-font-size-larger);
				border-bottom: var(--im-border);
			}
		}

		.service-info {
			flex: 1;
			display: flex;
			justify-content: center;
			padding: 20px;
			align-items: center;

			.info-card {
				border-radius: 20px;
				padding: 10px;
				position: relative;
				overflow: hidden;
				max-width: 600px;
				width: 100%;

				.avatar-section {
					text-align: center;
					margin-bottom: 20px;

					.head-image {
						border: 4px solid rgba(255, 255, 255, 0.8);
						box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
					}
				}

				.info-section {
					.name-header {
						display: flex;
						justify-content: center;
						align-items: center;
						gap: 8px;
						margin-bottom: 25px;
						padding-bottom: 15px;
						border-bottom: 1px solid rgba(0, 0, 0, 0.06);

						.service-name {
							font-size: 24px;
							font-weight: 600;
							color: var(--im-text-color);
							margin: 0;
						}

						.service-tag {
							padding: 0 6px;
							font-size: var(--im-font-size-small);
							color: var(--im-color-primary);
							border: 1px solid var(--im-color-primary);
							border-radius: 4px;
						}
					}

					.info-item-list {
						margin-bottom: 30px;

						.info-item {
							display: flex;
							align-items: center;
							padding: 12px 0;
							border-bottom: 1px solid rgba(0, 0, 0, 0.04);

							&:last-child {
								border-bottom: none;
							}

							.info-label {
								font-weight: 500;
								color: var(--im-text-color-light);
								font-size: 14px;
								min-width: 80px;
								margin-right: 20px;
							}

							.info-value {
								flex: 1;
								color: var(--im-text-color);
								font-size: 15px;
								line-height: 24px;
								text-align: left;
							}
						}
					}

					.btn-group {
						display: flex;
						gap: 12px;
						justify-content: center;
						flex-wrap: wrap;
					}
				}
			}
		}
	}
}
</style>
